"use client"

import { Text } from "@/elements"
import { cn } from "@/utils/classnames"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import * as Popover from "@radix-ui/react-popover"
import { useState } from "react"
import { ApolloQueryResult, FetchMoreOptions, FetchMoreQueryOptions } from "@apollo/client"
import { PageInfo } from "@/gql/graphql"
import { DropdownPopoverContent } from "./DropdownPopoverContent"

interface Props {
  onSelect: (item: { name: string; id: string }) => void
  value?: string | null
  options: ({ name: string; id: string } | undefined)[] | undefined
  placeholder?: string
  dropdownClassName?: string
  loading: boolean
  pageInfo?: Pick<PageInfo, "hasNextPage" | "endCursor">
  pageSize?: number
  networkStatus: number
  fetchMore?: (
    fetchMoreOptions: FetchMoreQueryOptions<any, any> & FetchMoreOptions<any, any>
  ) => Promise<ApolloQueryResult<any>>
}

export const Dropdown: React.FC<Props> = ({
  onSelect,
  value,
  options,
  placeholder,
  dropdownClassName,
  loading,
  pageInfo,
  pageSize,
  networkStatus,
  fetchMore,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  return (
    <Popover.Root modal={true} open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <Popover.Trigger asChild>
        <div
          className={cn(
            "flex w-full cursor-pointer flex-row items-center justify-between border-b pb-2 aria-disabled:pointer-events-none",
            dropdownOpen ? "border-primary-3" : "border-bg-3"
          )}>
          <Text className={cn("select-none", value ? "text-fg-1" : "text-bg-5")}>
            {value || placeholder || "Select an option"}
          </Text>
          {dropdownOpen ? <ChevronUpIcon height={22} /> : <ChevronDownIcon height={22} />}
        </div>
      </Popover.Trigger>

      <Popover.Content
        className="bg-bg-1"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
        sideOffset={5}>
        <DropdownPopoverContent
          onSelect={onSelect}
          options={options}
          networkStatus={networkStatus}
          toggleDropdown={setDropdownOpen}
          dropdownClassName={dropdownClassName}
          pageSize={pageSize}
          fetchMore={fetchMore}
          loading={loading}
          pageInfo={pageInfo}
        />
      </Popover.Content>
    </Popover.Root>
  )
}
